import React from 'react';
import './App.css';
import { BrowserRouter as Router, useNavigate } from "react-router-dom";
import AppRouter from "./components/routers/AppRouter";
import {ApolloClient, ApolloProvider, createHttpLink, from, InMemoryCache} from '@apollo/client';
import {setContext} from "@apollo/client/link/context";
import {onError} from "@apollo/client/link/error";
import { AuthProvider, useAuth } from './context/AuthContext';
import Cookies from 'js-cookie';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ApolloClientProvider = ({ children }: { children: React.ReactNode }) => {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
            graphQLErrors.forEach(({ message, extensions }) => {
                if (message === 'jwt expired' || extensions?.code === 'UNAUTHENTICATED') {
                    toast.error('Your session has expired. Please log in again.');
                    logout();
                    navigate('/login');
                } else {
                    toast.error(message);
                }
            });
        }
        if (networkError) {
            console.error(`[Network error]: ${networkError}`);
            toast.error('Network error occurred. Please try again.');
        }
    });

    const httpLink = createHttpLink({
        uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
        credentials: 'include',
        /*headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }*/
    });

    const authLink = setContext((_, { headers }) => {
        const token = Cookies.get('accessToken');
        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : "",
                'Content-Type': 'application/json',
                //'apollo-require-preflight': 'true',
                'Accept': 'application/json',
            }
        }
    });

    const client = new ApolloClient({
        link: from([errorLink, authLink, httpLink]),
        cache: new InMemoryCache({
            typePolicies: {
                // Tour-assigned guides carry per-tour isConfirmedByGuide; do not normalize
                // by _id alone or one tour's confirmation overwrites another's in cache.
                GuideOutput: {
                    keyFields: false,
                },
            },
        }),
        defaultOptions: {
            watchQuery: {
                fetchPolicy: 'network-only',
            },
            query: {
                fetchPolicy: 'network-only',
            },
        }
    });

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

const App = () => {
    return (
        <Router>
            <AuthProvider>
                <ApolloClientProvider>
                    <ToastContainer position="top-right" autoClose={3000} />
                    <AppRouter />
                </ApolloClientProvider>
            </AuthProvider>
        </Router>
    );
}

export default App;
